import type { FC } from 'react'

import { Icon, InfoBox, StyledText } from '@skytech/ui'
import { getPricePerInstallment } from '@skytech/utils'
import CheckCircleOutlineIcon from 'mdi-react/CheckCircleOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import { CartCostBar } from '@/components/cart/CartCostBar'
import { Translation } from '@/components/i18n/Translation'
import { statusStyleByWhiteLabel } from '@/components/util/style-constant'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'

type ProductsSummaryProps = {
  orderTotalCost: number
  coverAmount: number
  maxAmount: number
  paymentInterval: number
  displayMode: 'default' | 'grossSalaryScheme'
  coverUsed: number
}

export const ProductsSummary: FC<ProductsSummaryProps> = ({
  orderTotalCost,
  coverAmount,
  maxAmount,
  paymentInterval,
  displayMode,
  coverUsed,
}) => {
  const { whiteLabel } = useAppContext()
  const { formatNumber } = useFormattedNumberWithCurrency()
  const coverAvailable = coverAmount - coverUsed

  const coverPct = maxAmount ? coverAvailable / maxAmount : 0
  const usedPct = maxAmount ? orderTotalCost / maxAmount : 0

  const isSpendingLimitExceeded = orderTotalCost > maxAmount

  const coveredAmount = Math.min(orderTotalCost, coverAvailable)
  // amountToPay covers the salary deduction case and the gross salary scheme case,
  // in the first one we want to display the total amount to pay,
  // in the second one we want to display the monthly price which is calculated later with the getPricePerInstallment function
  const amountToPay = Math.max(0, orderTotalCost - coveredAmount)
  const grossSalaryMonthlyPrice = getPricePerInstallment(amountToPay, paymentInterval)

  return (
    <>
      {isSpendingLimitExceeded && (
        <InfoBox emphasis="error" className="mb-0.5 whitespace-normal sm:mb-2">
          <Translation
            id="Totalkosten overskrider maksbeløpet på {maxAmount}"
            maxAmount={formatNumber({ amount: maxAmount })}
          />
        </InfoBox>
      )}

      {orderTotalCost === 0 ? (
        <div>
          <StyledText colorMode="black" className="!leading-none">
            <Translation id="No products selected" />
          </StyledText>
        </div>
      ) : (
        amountToPay === 0 && (
          <div>
            <StyledText colorMode="black" className="!leading-none">
              <Icon colorMode="success" size="small" className="mr-1" isWhiteLabeled>
                <CheckCircleOutlineIcon />
              </Icon>
              <Translation id="order completely covered by employer" />
            </StyledText>
          </div>
        )
      )}

      {displayMode === 'default' && maxAmount - coverAmount > 0 && amountToPay > 0 && (
        <div>
          <StyledText colorMode="black" size="large" className="max-mobile:!text-lg !leading-none">
            <span
              className={twJoin(
                statusStyleByWhiteLabel[whiteLabel]?.warning.text ||
                  statusStyleByWhiteLabel.skytech!.warning.text
              )}
            >
              {formatNumber({ amount: amountToPay })}
            </span>
          </StyledText>{' '}
          <StyledText
            colorMode="black"
            size="regular-plus"
            className="max-mobile:!text-sm !leading-none"
          >
            <Translation id="sent for payroll deduction" />
          </StyledText>
        </div>
      )}

      {displayMode === 'grossSalaryScheme' &&
        maxAmount - amountToPay > 0 &&
        grossSalaryMonthlyPrice > 0 && (
          <div>
            <StyledText
              colorMode="black"
              size="large"
              className="max-mobile:!text-lg !leading-none"
            >
              <span
                className={twJoin(
                  statusStyleByWhiteLabel[whiteLabel]?.warning.text ||
                    statusStyleByWhiteLabel.skytech!.warning.text
                )}
              >
                {formatNumber({ amount: grossSalaryMonthlyPrice })}
              </span>
            </StyledText>{' '}
            {paymentInterval <= 1 ? (
              <StyledText
                colorMode="black"
                size="regular-plus"
                className="max-mobile:!text-sm !leading-none"
              >
                <Translation id="via payroll deduction" />
              </StyledText>
            ) : (
              <StyledText
                colorMode="black"
                size="regular-plus"
                className="max-mobile:!text-sm !leading-none"
              >
                <Translation
                  id="per month via payroll deduction ({paymentInterval} {paymentInterval, plural, one {month} other {months}})"
                  paymentInterval={paymentInterval}
                />
              </StyledText>
            )}
          </div>
        )}

      <CartCostBar
        isSpendingLimitExceeded={isSpendingLimitExceeded}
        coverPct={coverPct}
        usedPct={usedPct}
      />
    </>
  )
}
